import { createFileLogger } from './logger';
import { parseJwtPayload } from './jwtUtils';
import {
  ANALYTICS_SESSION_KEY,
  ANALYTICS_SESSION_ACTIVITY_KEY,
  analytics,
} from '../services/analytics/analytics';

const log = createFileLogger('LOGOUT_UTILS');

const ACCOUNTS_INDEX_KEY = 'river_accounts';
const ACCOUNT_PREFIX = 'river_account_';

/**
 * Remove the currently-active account from the multi-account switcher.
 * Must be called BEFORE the authToken is cleared so we can still read the userId.
 */
function removeActiveAccountFromSwitcher(): void {
  try {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const payload = parseJwtPayload(authToken);
    const userId = payload?.sub;
    if (!userId) return;

    localStorage.removeItem(`${ACCOUNT_PREFIX}${userId}`);

    const raw = localStorage.getItem(ACCOUNTS_INDEX_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    const updated = ids.filter(id => id !== userId);
    if (updated.length > 0) {
      localStorage.setItem(ACCOUNTS_INDEX_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(ACCOUNTS_INDEX_KEY);
    }

    log.log(`🧹 [LOGOUT] Removed account ${userId} from multi-account switcher`);
  } catch (e) {
    // Non-fatal — don't block the logout flow
    log.warn('[LOGOUT] Failed to remove account from switcher:', e);
  }
}

/**
 * Auth-related storage keys — single source of truth for logout cleanup.
 *
 * Add every key here when you introduce it. Keys are split by storage type so
 * clearAllAuthState can iterate them correctly.
 */
export const AUTH_STORAGE_KEYS = {
  // ── localStorage ──────────────────────────────────────────────────────────
  localStorage: {
    // Auth tokens
    tokens: ['authToken', 'id_token', 'refresh_token'],

    // Pending OAuth context (set before OAuth popup, consumed after)
    pending: [
      'pendingFunnelId',
      'pendingPersona',
      'pendingPageGroup',
      'pendingPageSlug',
      'pendingRedirectTo',
      'pendingTemplateId',
      'pendingIsSignup',
      'pendingWorkflowContext',
      'pendingWorkflowContext_timestamp',
      'pre_signup_onboarding_answers',
    ],

    // Session / redirect data
    session: [
      'redirectAfterLogin',
      'lastAuthRedirect',
      'userSpaceId',
      'userWelcomeDocId',
      'signupEmail',
    ],

    // GalBox status
    galbox: ['galbox-inactive', 'galbox-inactive-name'],
  },

  // ── sessionStorage ────────────────────────────────────────────────────────
  sessionStorage: [
    'pendingRedirectTo',
    'pendingTemplateId',
    'landing_hero_context',
    'onboarding_source',
    // Analytics session keys — must be cleared so the post-logout session
    // gets a fresh ID and does not inherit the pre-logout session.
    ANALYTICS_SESSION_KEY,
    ANALYTICS_SESSION_ACTIVITY_KEY,
  ],
} as const;

/**
 * Clear ALL auth-related data from localStorage and sessionStorage.
 */
export function clearAllAuthState(): void {
  log.log('🧹 [LOGOUT] Starting comprehensive auth state cleanup...');

  let clearedCount = 0;

  // Clear all localStorage keys
  const lsKeys = Object.values(AUTH_STORAGE_KEYS.localStorage).flat() as string[];
  lsKeys.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared localStorage: ${key}`);
    }
  });

  // Clear Auth0 SDK cache entries (@@auth0spajs@@::...) which live in localStorage
  // when cacheLocation='localstorage'. The Auth0 SDK manages its own key names so
  // we sweep by prefix rather than maintaining a hardcoded key.
  Object.keys(localStorage)
    .filter(key => key.startsWith('@@auth0spajs@@'))
    .forEach(key => {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared Auth0 SDK cache: ${key}`);
    });

  // Clear all sessionStorage keys
  AUTH_STORAGE_KEYS.sessionStorage.forEach(key => {
    if (sessionStorage.getItem(key) !== null) {
      sessionStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared sessionStorage: ${key}`);
    }
  });

  log.log(`🧹 [LOGOUT] Cleanup complete - cleared ${clearedCount} items`);
}

/**
 * Perform a complete logout:
 * 1. Clear all localStorage/sessionStorage
 * 2. Optionally call Auth0 logout
 * 3. Redirect to home
 */
export function performCompleteLogout(options: {
  auth0Logout?: (params: any) => void;
  hasAuth0User: boolean;
}): void {
  log.log('🚪 [LOGOUT] Starting complete logout process...');

  // Step 0: Remove the current account from the multi-account switcher BEFORE
  // clearing tokens (we need the authToken to read the userId).
  removeActiveAccountFromSwitcher();

  // Step 1: Reset analytics facade (emits session_ended, rotates session ID).
  // Must run before clearAllAuthState so the authToken is still available for
  // the session_ended beacon payload.
  analytics.reset();

  // Step 2: Clear all auth state
  clearAllAuthState();
  
  // Step 2: Auth0 logout if applicable
  if (options.hasAuth0User && options.auth0Logout) {
    log.log('🚪 [LOGOUT] Performing Auth0 logout...');
    options.auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  } else {
    // Step 3: Redirect to home (Auth0 logout handles redirect itself)
    log.log('🚪 [LOGOUT] Redirecting to home...');
    window.location.href = '/';
  }
}

